import { FC } from 'react';
import { Checkbox as MuiCheckbox, FormControlLabel } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconSquare, IconSquareCheck } from '@tabler/icons-react';

import CustomIconSquareMinus from './CustomIconSquareMinus';
import { CheckboxProps } from './types';

const Checkbox: FC<CheckboxProps> = ({
  checked = false,
  disabled = false,
  indeterminate = false,
  label = '',
  padding,
  ...rest
}) => {
  const theme = useTheme();

  const checkedOrIndeterminateIconStyles = {
    // `undefined` is for using the default theme color:
    color: disabled ? theme.palette.colors.grey[100] : undefined,

    fill: disabled ? theme.palette.colors.slate[100] : 'transparent',
  };

  const isCheckedOrIndeterminate = checked || indeterminate;

  return (
    <FormControlLabel
      disabled={disabled}
      control={
        <MuiCheckbox
          checked={checked}
          indeterminate={indeterminate}
          disabled={disabled}
          icon={
            <IconSquare
              {...checkedOrIndeterminateIconStyles}
              strokeWidth={1}
              color={
                disabled
                  ? theme.palette.colors.grey[100]
                  : theme.palette.colors.grey[500]
              }
            />
          }
          checkedIcon={
            <IconSquareCheck {...checkedOrIndeterminateIconStyles} />
          }
          indeterminateIcon={
            <CustomIconSquareMinus {...checkedOrIndeterminateIconStyles} />
          }
          sx={[
            {
              '&.MuiCheckbox-root': {
                padding,
              },

              ':hover': {
                '.icon': {
                  stroke: isCheckedOrIndeterminate
                    ? theme.palette.colors.purple[600]
                    : theme.palette.colors.grey[500],
                },

                backgroundColor: 'unset',
              },
              paddingRight: theme.spacing(1),
            },

            isCheckedOrIndeterminate && {
              '&.Mui-disabled': {
                // `:last-child` points to the checkmark (for checked state)
                // or the minus sign (for indeterminate state).
                // This is to avoid changing the color of the square symbol.
                '.icon :last-child': {
                  stroke: theme.palette.colors.grey[400],
                },
              },
            },
          ]}
          {...rest}
        />
      }
      sx={[
        {
          '.MuiFormControlLabel-label': {
            ...theme.typography.font14,
            letterSpacing: -0.1,
          },

          ':hover:not(.Mui-disabled)': {
            '.icon-tabler-square': {
              stroke: isCheckedOrIndeterminate
                ? theme.palette.colors.purple[600]
                : theme.palette.colors.grey[600],
            },
          },
        },

        // Don't grey out label if checked or inderetminate:
        isCheckedOrIndeterminate && {
          '.MuiFormControlLabel-label.Mui-disabled': {
            color: 'unset',
          },
        },
      ]}
      label={label}
    />
  );
};

export default Checkbox;
